import { useState, useEffect } from 'react';
import { getLookupCollectionFull } from '../services/firebase';

export interface CollectionPropertiesMap {
  [collectionName: string]: {
    isMultiSelect: boolean;
    isActive: boolean;
    description?: string;
  };
}

export function useLookupCollectionProperties(collectionNames: string[]) {
  const [propertiesMap, setPropertiesMap] = useState<CollectionPropertiesMap>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchProperties() {
      setLoading(true);
      const map: CollectionPropertiesMap = {};

      // Fetch properties in parallel batches to avoid overwhelming Firebase
      const batchSize = 10;
      for (let i = 0; i < collectionNames.length; i += batchSize) {
        const batch = collectionNames.slice(i, i + batchSize);
        const results = await Promise.all(
          batch.map(async (name) => {
            try {
              const data = await getLookupCollectionFull(name);
              return {
                name,
                isMultiSelect: data?.isMultiSelect ?? false,
                isActive: data?.isActive ?? true,
                description: data?.description,
              };
            } catch {
              return {
                name,
                isMultiSelect: false,
                isActive: true,
                description: undefined,
              };
            }
          })
        );

        if (cancelled) return;

        for (const result of results) {
          map[result.name] = {
            isMultiSelect: result.isMultiSelect,
            isActive: result.isActive,
            description: result.description,
          };
        }
      }

      if (!cancelled) {
        setPropertiesMap(map);
        setLoading(false);
      }
    }

    if (collectionNames.length > 0) {
      fetchProperties();
    } else {
      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [collectionNames.join(',')]); // Re-fetch if collection names change

  return { propertiesMap, loading };
}
